import { Fragment, PureComponent } from 'react'
import PropTypes from 'prop-types'
import withStyles from 'beam/dist/styles'
import { CircularProgress } from 'beam/dist/Progress'
import Typography from 'beam/dist/Typography'
import PlayerPiano from '../PlayerPiano/PlayerPiano'
import { ConditionalWrap } from 'beam/dist/utils'
import classNames from 'classnames'
import { AutoSizer } from 'react-virtualized'
import { withTranslation } from 'react-i18next'
import { range } from 'lodash'
import SentimentTape from './SentimentTape'
import { PANEL_SIZES } from '../MediaPlayer/helpers'

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 100%',
  },
  noData: {
    justifyContent: 'center',
    alignItems: 'center',
    flexGrow: 1,
  },
  panelRoot: {
    boxShadow: 'none',
  },
  panelBody: {
    padding: theme.spacing(1, 0),
  },
  channelLabel: {
    position: 'absolute',
    zIndex: 1000,
    width: theme.spacing(1),
    height: theme.spacing(2),
    backgroundColor: 'rgba(250,250,250,0.85)', //Equivalent to theme.palette.grey[50]
    textAlign: 'center',
    fontSize: '12px',
    color: theme.palette.grey[400],
  },
})

/**
 * This component does not concern it self with actually requesting the transcription (with sentiment data)
 * because as of writing this it only gets rendered when the transcription panel gets rendered - which already
 * loads the transcription
 */
class SentimentContent extends PureComponent {
  static propTypes = {
    t: PropTypes.func,
    classes: PropTypes.object,
    theme: PropTypes.object,
    zoomed: PropTypes.bool,
    loading: PropTypes.bool.isRequired,
    error: PropTypes.bool.isRequired,
    callDuration: PropTypes.number,
    currentTime: PropTypes.number,
    setTime: PropTypes.func,
    texts: PropTypes.array,
    startTime: PropTypes.number,
  }

  static defaultProps = {
    t: s => s,
    classes: {},
    texts: [],
    callDuration: 0,
    currentTime: 0,
    setTime: t => t,
    zoomed: true,
    startTime: 0,
  }

  static renderLoading() {
    return <CircularProgress />
  }

  static renderError(t) {
    return (
      <div>
        <Typography color="secondary" align="center">
          {t('error2')}
        </Typography>
        <Typography color="secondary" align="center">
          {t('sentimentError')}
        </Typography>
      </div>
    )
  }

  static renderNoData(t) {
    return <Typography>{t('noSentimentData')}</Typography>
  }

  // assumes there is either one or two channels (no more)
  static getTotalChannels = texts => (texts.some(({ channel }) => channel === 2) ? 2 : 1)

  getColumnWidth = () => this.props.theme.spacing(3)
  getX = channel =>
    channel === 1 ? this.props.theme.spacing(1) : 3 * this.props.theme.spacing(1) + this.getColumnWidth()
  getChannelLabelPosition = channel => this.getX(channel) + this.getColumnWidth() / 2 - this.props.theme.spacing(0.5)

  renderData(totalChannels) {
    const {
      theme,
      texts,
      classes,
      callDuration,
      currentTime,
      setTime,
      zoomed,
      startTime,
    } = this.props

    const panelWidth = theme.spacing(PANEL_SIZES.SENTIMENT)

    return (
      <AutoSizer disableWidth>
        {({ height }) => {
          // If zoomed a pixel every 1/10th of a second
          const graphHeight = zoomed ? Math.max(callDuration / 100, height) : height
          const heightUnitsPerMs = graphHeight / callDuration

          return (
            <div style={{ height: `${height}px`, width: `${panelWidth}px` }}>
              <ConditionalWrap
                condition={zoomed}
                wrap={children => (
                  <PlayerPiano
                    duration={callDuration}
                    currentTime={currentTime}
                    label={
                      <Fragment>
                        {range(1, totalChannels + 1).map(i => (
                          <div
                            className={classes.channelLabel}
                            style={{ left: this.getChannelLabelPosition(i) }}
                            key={i}
                          >
                            {i}
                          </div>
                        ))}
                      </Fragment>
                    }
                  >
                    {children}
                  </PlayerPiano>
                )}
              >
                <SentimentTape
                  width={panelWidth}
                  graphHeight={graphHeight}
                  heightUnitsPerMs={heightUnitsPerMs}
                  totalChannels={totalChannels}
                  texts={texts}
                  startTime={startTime}
                  callDuration={callDuration}
                  setTime={setTime}
                />
              </ConditionalWrap>
            </div>
          )
        }}
      </AutoSizer>
    )
  }

  render() {
    const { renderLoading, renderError, renderNoData, getTotalChannels } = SentimentContent
    const { t, classes, loading, error, texts } = this.props
    const noData = loading || error || texts.length === 0
    const totalChannels = noData ? 0 : getTotalChannels(texts)

    return (
      <div className={classNames(classes.root, noData && classes.noData)}>
        {(loading && renderLoading(t)) ||
          (error && renderError(t)) ||
          (texts.length === 0 && renderNoData(t)) ||
          this.renderData(totalChannels)}
      </div>
    )
  }
}

export { SentimentContent as UnstyledSentimentContent }
export default withTranslation()(withStyles(styles, { withTheme: true })(SentimentContent))
